import { useState, useEffect } from 'react';

interface ServiceModalProps {
  service: string | null;
  onClose: () => void;
}

export default function ServiceModal({ service, onClose }: ServiceModalProps) {
  const [postcode, setPostcode] = useState('');
  const [postcodeStatus, setPostcodeStatus] = useState<'idle' | 'covered' | 'notCovered'>('idle');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // Check if we're in Stage B (postcode validated and covered)
  const isStageB = postcodeStatus === 'covered';

  const serviceCategories = [
    { id: 'handyman', name: 'Handyman', icon: 'ri-tools-fill' },
    { id: 'plumbing', name: 'Plumbing', icon: 'ri-drop-fill' },
    { id: 'electrical', name: 'Electrical', icon: 'ri-flashlight-fill' },
    { id: 'cleaning', name: 'Cleaning', icon: 'ri-brush-fill' },
    { id: 'waste', name: 'Waste Removal', icon: 'ri-delete-bin-fill' },
    { id: 'transportation', name: 'Transport & Moving', icon: 'ri-truck-fill' }
  ];

  const toggleService = (serviceName: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceName) 
        ? prev.filter(s => s !== serviceName)
        : [serviceName]
    );
  };

  const handleContinue = () => {
    if (selectedServices.length > 0 && isStageB) {
      // Find the category by name and get its ID
      const selectedCategory = serviceCategories.find(cat => cat.name === selectedServices[0]);
      
      if (selectedCategory) {
        // Navigate with the category ID (not the name)
        window.REACT_APP_NAVIGATE?.(`/service-request?category=${selectedCategory.id}&postcode=${postcode}`);
        onClose();
      }
    }
  };

  // Validate postcode when user types
  useEffect(() => {
    if (postcode.length >= 5) {
      // Simple validation - in production, this would call an API
      const validPostcodePattern = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
      if (validPostcodePattern.test(postcode)) {
        setPostcodeStatus('covered');
      } else {
        setPostcodeStatus('notCovered');
      }
    } else {
      setPostcodeStatus('idle');
    }
  }, [postcode]);

  useEffect(() => {
    if (service) {
      setPostcode('');
      setPostcodeStatus('idle');
      setSelectedServices([]);
    }
  }, [service]);

  if (!service) return null;

  const selectedCategory = selectedServices.length > 0 ? selectedServices[0] : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {/* Main booking module container with two-stage color system */}
      <div
        className={`
          relative rounded-2xl border-2 overflow-hidden transition-all duration-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto
          ${
            isStageB
              ? 'bg-slate-800 border-slate-700 shadow-[0_0_30px_rgba(249,115,22,0.3)]'
              : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 shadow-lg'
          }
        `}
      >
        {/* Top accent bar - orange */}
        <div className="h-1.5 bg-gradient-to-r from-orange-500 to-orange-600"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
        >
          <i className={`ri-close-line text-xl ${isStageB ? 'text-white' : 'text-slate-700'}`}></i>
        </button>

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h2
              className={`text-3xl font-bold mb-3 transition-colors duration-300 ${
                isStageB ? 'text-white' : 'text-slate-800'
              }`}
            >
              Book a Trusted Tradesperson
            </h2>
            <p
              className={`text-lg transition-colors duration-300 ${
                isStageB ? 'text-slate-200' : 'text-slate-600'
              }`}
            >
              {isStageB
                ? 'Great — now choose your category'
                : 'Start here: Enter your postcode'}
            </p>
          </div>

          {/* Postcode Input Section */}
          <div className="mb-8">
            <label
              className={`block text-sm font-semibold mb-3 transition-colors duration-300 ${
                isStageB ? 'text-white' : 'text-slate-700'
              }`}
            >
              Your Postcode
            </label>
            <div className="relative">
              <input
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                placeholder="e.g., SW1A 1AA"
                className={`
                  w-full px-5 py-4 rounded-xl font-medium text-lg transition-all duration-300
                  focus:outline-none focus:ring-2 focus:ring-orange-500
                  ${
                    isStageB
                      ? 'bg-white text-slate-800 border-2 border-white'
                      : 'bg-white text-slate-800 border-2 border-blue-300'
                  }
                `}
              />
              {isStageB && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 text-white text-sm font-semibold rounded-full">
                    <i className="ri-check-line"></i>
                    Area covered
                  </span>
                </div>
              )}
            </div>

            {/* Not covered message */}
            {postcodeStatus === 'notCovered' && (
              <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-300 rounded-xl">
                <p className="text-orange-700 font-semibold text-center">
                  Sorry, we're not in your area yet.
                </p>
              </div>
            )}
          </div>

          {/* Service Categories Grid */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {serviceCategories.map((category) => {
              const isSelected = selectedServices.includes(category.name);
              return (
                <button
                  key={category.id}
                  onClick={() => toggleService(category.name)}
                  disabled={!isStageB}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 h-[100px] ${
                    isSelected
                      ? 'bg-[#fe7a14] border-[#fe7a14] text-white'
                      : isStageB
                      ? 'bg-white border-[#fe7a14] text-gray-700 hover:border-[#ff8c3a] cursor-pointer'
                      : 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                >
                  <i
                    className={`${category.icon} text-2xl mb-2 ${
                      isSelected ? 'text-white' : isStageB ? 'text-[#fe7a14]' : 'text-gray-400'
                    }`}
                  ></i>
                  <span className="text-[10px] font-medium text-center leading-tight">
                    {category.name}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={!selectedCategory || !isStageB}
            className={`
              w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 whitespace-nowrap
              ${
                selectedCategory && isStageB
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 hover:shadow-xl cursor-pointer'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }
            `}
          >
            Continue to Service Details
          </button>
        </div>
      </div>
    </div>
  );
}
